package com.elan.edubridge.ui.shell

/**
 * `/terms`, `/privacy`, `/support`, `/account-deletion` — 계획 §4:79·§4:91(P0, 출시 차단 조건).
 *
 * 문안의 사실 근거는 `docs/release/data-inventory-v1.0.0.md`(코드에서 추출한 수집 항목 전수 조사)이고,
 * 원본 문안은 `docs/release/legal-copy-v1.0.0.md`에 있다. **이 파일과 그 두 문서는 같이 고쳐야 한다** —
 * 여기 있는 보관 기간·삭제 동작은 전부 서버 코드의 실제 동작을 옮긴 것이라 한쪽만 바뀌면 거짓말이
 * 된다(예: 유예 7일 = `functions/src/accountDeletion.ts`의 `DELETION_GRACE_DAYS`).
 *
 * 구조가 `enum`의 `body` 한 줄에서 [Section] 목록으로 바뀐 이유는 단순하다. 스토어 심사에 통과하는
 * 법적 고지는 문단 하나로 끝나지 않고, 소제목·표·목록이 필요하다. 표는 새 컴포넌트를 만들지 않고
 * 기존 `.eb-table`을 그대로 쓴다.
 *
 * **자리표시자 배지는 없다.** "이 페이지는 자리표시자입니다"라는 자기 선언이야말로 심사 거절 사유이며,
 * 초안이라는 사실 자체는 문제가 되지 않는다. 대신 문서 하단에 최종 수정일과 문의처를 둔다.
 *
 * `[대괄호]`로 남은 값(상호·주소·사업자등록번호·개인정보 보호책임자)은 코드 어디에도 없는 사업자
 * 정보다. 그럴듯한 값을 지어내면 그 자체가 허위 표시이므로 눈에 띄게 비워 둔다.
 *
 * `/account-deletion`은 로그인 없이도 열람 가능한 **공개 안내 페이지**다(스토어 심사 요건). 로그인 후
 * 실제 삭제를 수행하는 화면은 `ui/member/AccountDeletionPage.kt`(`/app/me/settings` 하위)가 맡는다.
 */
object LegalPages {
    /** 결제 문안이 바뀐 약관·방침만 개정일을 올리고, 나머지 공개 페이지 날짜는 유지한다. */
    private const val LAST_UPDATED = "2026-08-17"
    private const val BILLING_LEGAL_LAST_UPDATED = "2026-08-19"

    internal const val SUPPORT_EMAIL = "dreamnote1010@gmail.com"

    /**
     * 사업자 정보. 출처는 사업자등록증·통신판매업신고증이며 **증명서에 함께 있는
     * 주민등록번호·생년월일은 여기 옮기지 않는다** — 표시의무 대상이 아니고, 공개 페이지에
     * 올리는 순간 그 자체가 개인정보 유출이다.
     *
     * 주소는 **사업자등록증상 사업장 소재지**를 쓴다. 통신판매업신고증에는 자택 주소가
     * 적혀 있어 그대로 게시하면 대표자의 거주지가 공개된다.
     */
    internal const val COMPANY = "엘란(ELAN)"
    internal const val REPRESENTATIVE = "유병철"
    internal const val ADDRESS = "경기도 용인시 수지구 현암로 148, 602-S35호(죽전동, 죽전스카이프라자)"
    internal const val BUSINESS_NUMBER = "333-09-03235"

    /** 「전자상거래 등에서의 소비자보호에 관한 법률」 제12조 신고. */
    internal const val MAIL_ORDER_NUMBER = "제2026-용인수지-0051호"

    /** 개인사업자라 대표가 보호책임자를 겸한다(개인정보 보호법 제31조). */
    private const val PRIVACY_OFFICER = "유병철"
    private const val PRIVACY_OFFICER_TITLE = "대표"

    /**
     * 연락 수단은 이메일 단일 채널이다(사용자 결정, 2026-08-17). 전화번호를 두지 않으므로
     * 전화 행을 자리표시자로 남기지 않고 표에서 아예 뺀다 — 빈 연락처는 없는 것만 못하다.
     */
    private const val PRIVACY_OFFICER_EMAIL = SUPPORT_EMAIL
    private const val EFFECTIVE_DATE = "2026년 8월 17일"

    enum class Page(val title: String, val lead: String) {
        TERMS(
            "이용약관",
            "EduBridge 서비스의 이용 조건과 회사·이용자의 권리와 의무를 정합니다.",
        ),
        PRIVACY(
            "개인정보처리방침",
            "EduBridge가 어떤 개인정보를 무엇을 위해 처리하고 언제 파기하는지 안내합니다.",
        ),
        SUPPORT(
            "고객지원",
            "문의 방법과 자주 묻는 문제의 해결 방법을 안내합니다.",
        ),
        ACCOUNT_DELETION(
            "계정 삭제 안내",
            "계정 삭제를 요청하는 방법과, 삭제하면 실제로 무엇이 사라지고 무엇이 남는지 안내합니다.",
        ),
    }

    private fun lastUpdated(page: Page) = when (page) {
        Page.TERMS, Page.PRIVACY -> BILLING_LEGAL_LAST_UPDATED
        Page.SUPPORT, Page.ACCOUNT_DELETION -> LAST_UPDATED
    }

    /**
     * `--document` 수식자는 측정 폭만 넓힌다. 같은 `eb-legal` 블록을 쓰는 보호자 동의 대기 화면은
     * 카드 한 장짜리라 720px가 맞고, 여기는 문단이 수십 개라 그 폭으로는 줄이 너무 짧아진다.
     */
    fun render(page: Page) {
        renderPage("eb-legal eb-legal--document", html(page))
    }

    /** 소제목 하나와 그 아래 블록들. [id]는 목차 앵커이자 딥링크 대상이다. */
    private class Section(val id: String, val heading: String, val blocks: List<String>)

    private fun section(id: String, heading: String, vararg blocks: String) =
        Section(id, heading, blocks.toList())

    // ---- 블록 헬퍼. 전부 이 파일 안의 신뢰된 리터럴만 다루므로 이스케이프 대상이 아니다. ----

    private fun p(text: String) = "<p>$text</p>"

    private fun bullets(vararg items: String) =
        items.joinToString("", "<ul class=\"eb-legal__list\">", "</ul>") { "<li>$it</li>" }

    private fun numbers(vararg items: String) =
        items.joinToString("", "<ol class=\"eb-legal__list eb-legal__list--ordered\">", "</ol>") {
            "<li>$it</li>"
        }

    /** 놓치면 안 되는 문단. 새 색을 만들지 않고 기존 warning 토큰만 빌린다. */
    private fun callout(text: String) = "<p class=\"eb-legal__callout\">$text</p>"

    private fun subheading(text: String) = "<h3>$text</h3>"

    private fun table(headers: List<String>, rows: List<List<String>>): String {
        val head = headers.joinToString("") { "<th scope=\"col\">$it</th>" }
        val body = rows.joinToString("") { row ->
            row.joinToString("", "<tr>", "</tr>") { "<td>$it</td>" }
        }
        return """
            <div class="eb-table-scroll">
                <table class="eb-table"><thead><tr>$head</tr></thead><tbody>$body</tbody></table>
            </div>
        """.trimIndent()
    }

    private fun mailto(address: String) =
        "<a class=\"eb-table__link\" href=\"mailto:$address\">$address</a>"

    private fun link(href: String, label: String) =
        "<a class=\"eb-table__link\" href=\"$href\">$label</a>"

    // ---- 렌더 ----

    private fun html(page: Page): String {
        val sections = sections(page)
        return """
            <a class="eb-legal__brand" href="/">${icon("icon-bridge-logo", "lg")}EduBridge</a>
            <article class="eb-legal__card">
                <div class="eb-legal__header">
                    <h1>${page.title}</h1>
                </div>
                <p class="eb-meta">최종 수정일 ${lastUpdated(page)}</p>
                <p class="eb-legal__lead">${page.lead}</p>
                ${tableOfContents(sections)}
                <div class="eb-legal__body">
                    ${sections.joinToString("") { renderSection(it) }}
                </div>
                ${footer(page)}
            </article>
            <a class="eb-button eb-button--ghost" href="/">홈으로 돌아가기</a>
        """.trimIndent()
    }

    /** 짧은 페이지에 목차를 붙이면 본문보다 목차가 길어진다. */
    private fun tableOfContents(sections: List<Section>): String {
        if (sections.size < 6) return ""
        val items = sections.joinToString("") {
            "<li><a href=\"#${it.id}\">${it.heading}</a></li>"
        }
        return """
            <nav class="eb-legal__toc" aria-label="목차">
                <h2>목차</h2>
                <ol>$items</ol>
            </nav>
        """.trimIndent()
    }

    private fun renderSection(section: Section): String = """
        <section class="eb-legal__section" id="${section.id}">
            <h2>${section.heading}</h2>
            ${section.blocks.joinToString("")}
        </section>
    """.trimIndent()

    /**
     * 심사자와 이용자가 마지막으로 확인하는 두 가지 — 언제 고쳤는지, 어디로 물어보는지.
     * 자리표시 배지 대신 이것이 페이지의 신뢰 근거다.
     */
    private fun footer(page: Page): String {
        val related = when (page) {
            Page.TERMS -> listOf("/privacy" to "개인정보처리방침", "/support" to "고객지원")
            Page.PRIVACY -> listOf("/terms" to "이용약관", "/account-deletion" to "계정 삭제 안내")
            Page.SUPPORT -> listOf("/terms" to "이용약관", "/privacy" to "개인정보처리방침")
            Page.ACCOUNT_DELETION -> listOf("/privacy" to "개인정보처리방침", "/support" to "고객지원")
        }
        val links = related.joinToString("") { (href, label) ->
            "<li>${link(href, label)}</li>"
        }
        return """
            <footer class="eb-legal__footer">
                <p class="eb-meta">최종 수정일 ${lastUpdated(page)}</p>
                <p class="eb-meta">문의: ${mailto(SUPPORT_EMAIL)}</p>
                <ul class="eb-legal__footer-links">$links</ul>
            </footer>
        """.trimIndent()
    }

    // ---- 문안 ----

    private fun sections(page: Page): List<Section> = when (page) {
        Page.TERMS -> terms()
        Page.PRIVACY -> privacy()
        Page.SUPPORT -> support()
        Page.ACCOUNT_DELETION -> accountDeletion()
    }

    private fun privacy(): List<Section> = listOf(
        section(
            "collected", "1. 수집하는 개인정보 항목과 이용 목적",
            p(
                "$COMPANY(이하 \"회사\")는 EduBridge 학원 출결·알림 서비스(이하 \"서비스\") 제공을 " +
                    "위해 다음 개인정보를 처리합니다.",
            ),
            subheading("가. 계정 가입 시"),
            table(
                listOf("구분", "항목", "이용 목적"),
                listOf(
                    listOf("필수", "이메일 주소", "계정 식별, 로그인, 공지 발송"),
                    listOf("필수", "이름", "학원 내 본인 식별, 알림 문구 표시"),
                    listOf(
                        "필수",
                        "생년월일",
                        "만 14세 미만 가입 차단",
                    ),
                    listOf(
                        "필수",
                        "소셜 로그인 원본 정보(제공자 구분, 제공자측 사용자 식별자, 원본 이름·사진)",
                        "소셜 계정 연결 유지",
                    ),
                    listOf("선택", "프로필 사진", "프로필 표시"),
                ),
            ),
            p(
                "소셜 로그인으로 받은 원본 정보는 참고용으로만 보관하며 화면 표시에 사용하지 않습니다. " +
                    "서비스 안에서 이름이나 사진을 바꾸어도 원본 소셜 계정의 프로필은 변경되지 않습니다.",
            ),
            subheading("나. 학원 이용 시 (학원이 등록·관리하는 정보)"),
            table(
                listOf("구분", "항목", "이용 목적"),
                listOf(
                    listOf("필수", "학생 이름, 학년", "출결 관리, 수강생 명부"),
                    listOf("선택", "학생 전화번호", "학원의 연락"),
                    listOf("선택", "보호자 이름·전화번호·관계", "비상 연락, 보호자 초대"),
                    listOf(
                        "필수",
                        "출결 기록(등원·하원 시각, 출결 상태, 반, 처리 경로, 처리자)",
                        "출결 기록·조회·알림",
                    ),
                    listOf("필수", "보호자와 학생의 연결 정보", "출결 알림 수신 대상 판정"),
                ),
            ),
            callout(
                "이 구역의 정보는 <strong>학원이 개인정보처리자</strong>이고 회사는 학원의 위탁을 받아 " +
                    "처리하는 수탁자입니다. 자세한 내용은 8항을 참고해 주십시오.",
            ),
            subheading("다. 서비스 이용 과정에서 생성되는 정보"),
            table(
                listOf("구분", "항목", "이용 목적"),
                listOf(
                    listOf("선택", "푸시 알림 등록 토큰, 기기 구분(Android/iOS)", "등하원·공지 알림 발송"),
                    listOf("필수", "알림 발송 이력(수신자 식별자, 발송 성공·실패 결과)", "중복 발송 차단, 발송 품질 관리"),
                    listOf("선택", "게시판 글·댓글·공지 본문과 작성자 정보", "학원 커뮤니케이션"),
                    listOf("선택", "첨부 파일(JPEG·PNG·WebP·PDF, 파일당 10MB 이하)", "게시글 첨부"),
                    listOf("선택", "신고 내역(대상, 사유, 신고자)", "게시판 운영과 신고 처리"),
                    listOf("선택", "일정(제목, 설명, 시각)", "학원·개인 캘린더"),
                    listOf("필수", "감사 기록(행위, 행위자, 대상, 변경 전후 값, 시각)", "권한·개인정보 변경 이력의 무결성 확보"),
                ),
            ),
            subheading("라. 학원 개설을 신청하는 경우"),
            p(
                "학원명(필수)과 연락처·원장 이름·주소·소개(선택)를 수집하며 운영자 심사 목적으로만 " +
                    "사용합니다.",
            ),
            subheading("마. 학원 구독을 결제하는 경우"),
            p(
                "Google로부터 구독 상태·상품 종류·구매 식별자(가명화된 토큰)·만료일을 수신해 구독 " +
                    "상태 확인·학원 기능 활성화 목적으로만 처리하며, 구독 종료 후 정산·분쟁 대응 " +
                    "기간까지 보유합니다.",
            ),
            subheading("바. 회사가 처리하지 않는 정보"),
            p(
                "회사는 다음을 처리하지 않습니다: 주민등록번호, 위치정보, 결제카드 정보(구독 결제는 " +
                    "Google Play가 처리하며, 회사는 카드번호 등 결제수단 정보에 접근할 수 없습니다), " +
                    "생체정보, 사상·신념·건강 등 민감정보, 광고 식별자. " +
                    "<strong>분석 도구나 오류 수집 도구(Analytics·Crashlytics)를 사용하지 않습니다.</strong>",
            ),
        ),
        section(
            "basis", "2. 개인정보의 처리 근거",
            table(
                listOf("근거", "해당 항목"),
                listOf(
                    listOf("정보주체의 동의(법 제15조제1항제1호)", "계정 가입 정보, 푸시 알림 토큰, 선택 항목"),
                    listOf("계약의 이행(법 제15조제1항제4호)", "서비스 제공에 필수적인 계정·출결·알림 처리"),
                    listOf(
                        "법령상 의무 이행 및 정당한 이익(법 제15조제1항제2호·제6호)",
                        "감사 기록, 파기 기록, 부정 이용 방지",
                    ),
                ),
            ),
        ),
        section(
            "retention", "3. 개인정보의 보유 및 이용기간",
            table(
                listOf("구분", "보유기간"),
                listOf(
                    listOf(
                        "계정·프로필 정보",
                        "계정 삭제 시까지. 삭제 요청 후 <strong>7일의 유예기간</strong>을 거쳐 파기",
                    ),
                    listOf(
                        "학원의 학생 명부·출결 기록",
                        "학원의 서비스 이용기간. 이용 종료 후 30일의 데이터 반환 준비기간을 거쳐 파기",
                    ),
                    listOf("푸시 알림 등록 토큰", "로그아웃 또는 계정 삭제 시 즉시 삭제"),
                    listOf(
                        "게시판 글·댓글·공지",
                        "삭제 시 본문이 즉시 비워지며, 삭제 표시가 남은 문서는 학원 데이터 수명주기를 따름",
                    ),
                    listOf("첨부 파일 업로드 임시 저장분", "1일 후 자동 삭제"),
                    listOf("QR 스캔 소비 기록", "24시간"),
                    listOf("초대 코드", "발급 후 기본 72시간(최대 14일) 경과 시 사용 불가"),
                    listOf("감사 기록", "계정 삭제 후에도 보존(법 제21조제1항 단서)"),
                    listOf(
                        "계정 파기 기록",
                        "계정 삭제 후에도 보존(표준 개인정보 보호지침 제10조제3항·제4항)",
                    ),
                ),
            ),
            p("관계 법령이 별도의 보존기간을 정한 경우 해당 기간 동안 분리하여 보관합니다."),
        ),
        section(
            "destruction", "4. 개인정보의 파기 절차 및 방법",
            numbers(
                "<strong>파기 절차</strong> — 보유기간이 지나거나 처리 목적이 달성되면 지체 없이 " +
                    "파기합니다. 계정 삭제 요청은 요청일로부터 7일이 지난 뒤 매일 03시(한국시간)에 " +
                    "실행되는 자동 파기 작업이 처리합니다.",
                "<strong>파기 방법</strong> — 전자적 파일은 복구·재생이 불가능한 방법으로 영구 " +
                    "삭제합니다. 인증 계정, 데이터베이스 문서, 저장소 파일을 각각 삭제합니다.",
                "<strong>파기 기록</strong> — 파기 일시와 파기 건수를 기록으로 남기며, 이 기록에는 " +
                    "파기된 개인정보의 값 자체가 포함되지 않습니다.",
                "<strong>파기하지 않는 것</strong> — 감사 기록과 파기 기록은 남습니다. 학원이 " +
                    "보존의무를 지는 수강생 명부와 출결 기록은 계정과의 연결만 해제하고 학원의 기록으로 " +
                    "남습니다.",
            ),
            p("무엇이 남고 무엇이 사라지는지는 ${link("/account-deletion", "계정 삭제 안내")}에 상세히 적어 두었습니다."),
        ),
        section(
            "children", "5. 만 14세 미만의 가입 제한",
            numbers(
                "서비스는 <strong>만 14세 미만</strong>의 가입을 허용하지 않습니다. 가입 단계에서 " +
                    "생년월일로 확인하며, 우회 가입이 확인되면 해당 계정을 즉시 비활성화합니다.",
            ),
        ),
        section(
            "third-party", "6. 개인정보의 제3자 제공",
            p("회사는 정보주체의 개인정보를 <strong>제3자에게 제공하지 않습니다.</strong> 다만 다음은 예외입니다."),
            bullets(
                "정보주체가 사전에 동의한 경우",
                "법령에 특별한 규정이 있거나 수사기관이 법령에 정해진 절차와 방법에 따라 요구하는 경우",
            ),
            p(
                "<strong>학원 안에서의 정보 공유는 제3자 제공이 아닙니다.</strong> 학생의 출결 기록은 " +
                    "그 학생 본인과, 학원이 연결한 보호자, 그리고 해당 학원의 교직원만 열람할 수 " +
                    "있습니다. 보호자 연결이 해지되면 이미 전달된 출결 내역도 그 보호자의 화면에서 " +
                    "즉시 삭제됩니다.",
            ),
        ),
        section(
            "processor", "7. 개인정보 처리업무의 위탁",
            table(
                listOf("수탁자", "위탁 업무"),
                listOf(
                    listOf(
                        "Google LLC",
                        "계정 인증, 데이터베이스 운영, 파일 저장, 서버리스 애플리케이션 실행, 푸시 알림 발송",
                    ),
                ),
            ),
            p(
                "회사는 위탁계약 시 개인정보 보호 관련 법령 준수, 재위탁 제한, 안전성 확보조치, " +
                    "손해배상 등의 책임을 문서로 정하고 수탁자가 이를 준수하는지 감독합니다.",
            ),
        ),
        section(
            "academy-role", "8. 학원과 회사의 관계",
            numbers(
                "학원이 서비스에 등록·관리하는 학생 명부, 출결 기록, 보호자 연락처에 대하여 " +
                    "<strong>개인정보처리자는 해당 학원</strong>이며, 회사는 학원의 위탁을 받아 " +
                    "처리하는 <strong>수탁자</strong>입니다.",
                "이 정보에 대한 열람·정정·삭제 요구는 <strong>소속 학원에 먼저</strong> 해 주십시오. " +
                    "회사에 직접 요구하시는 경우 회사는 학원에 이를 전달하고 처리 결과를 안내합니다.",
                "「개인정보 보호법」 제26조제2항에 따라 위탁업무의 내용과 수탁자를 공개할 의무는 " +
                    "학원에 있습니다. 회사는 학원이 이 의무를 이행할 수 있도록 안내 자료를 제공합니다.",
            ),
        ),
        section(
            "overseas", "9. 개인정보의 국외 이전",
            p(
                "회사는 서비스 제공을 위해 다음과 같이 개인정보를 국외로 이전합니다. 이는 " +
                    "「개인정보 보호법」 제28조의8제1항제3호 가목에 따라 <strong>정보주체와의 계약 " +
                    "이행에 필요한 처리위탁·보관</strong>에 해당하며, 같은 조 제2항 각 호의 사항을 본 " +
                    "방침에 공개함으로써 별도의 동의를 갈음합니다.",
            ),
            table(
                listOf("구분", "내용"),
                listOf(
                    listOf("이전받는 자", "Google LLC"),
                    listOf(
                        "연락처",
                        link("https://policies.google.com/", "policies.google.com"),
                    ),
                    listOf("이전되는 국가", "미국(미합중국)"),
                    listOf("이전 일시 및 방법", "서비스 이용 시점에 정보통신망을 통해 실시간으로 전송"),
                    listOf(
                        "이전되는 항목",
                        "본 방침 1항에 기재된 모든 항목(계정·프로필, 학생 명부, 출결 기록, " +
                            "보호자 연락처, 게시판 콘텐츠와 첨부 파일, 푸시 알림 토큰, 감사 기록)",
                    ),
                    listOf(
                        "이전받는 자의 이용 목적",
                        "계정 인증, 데이터베이스 운영, 파일 저장, 서버리스 애플리케이션 실행, 푸시 알림 발송",
                    ),
                    listOf("보유·이용 기간", "본 방침 3항의 보유기간과 동일하며, 위탁계약 종료 시까지"),
                    listOf(
                        "이전 거부 방법 및 절차",
                        "개인정보 보호책임자(12항)에게 이메일로 거부 의사를 통지할 수 있습니다. " +
                            "다만 서비스의 인증·데이터 저장·알림 발송이 전적으로 이 기반 위에서 " +
                            "동작하므로 <strong>거부하시는 경우 서비스를 이용하실 수 없으며</strong>, " +
                            "이 경우 계정 삭제를 요청하실 수 있습니다.",
                    ),
                ),
            ),
            callout(
                "<strong>저장 위치에 관한 안내</strong> — 데이터베이스, 파일 저장소, 애플리케이션 실행 " +
                    "환경은 대한민국 서울 리전(asia-northeast3)에 배치되어 있습니다. 다만 이를 운영하는 " +
                    "주체가 미국 법인인 Google LLC이고, 계정 인증과 푸시 알림은 리전을 지정할 수 없는 " +
                    "글로벌 서비스이므로 회사는 이를 국외 이전으로 보아 위와 같이 고지합니다.",
            ),
            p(
                "웹 화면은 글꼴 파일을 외부 콘텐츠 전송 네트워크(jsDelivr)에서 내려받습니다. 이 " +
                    "과정에서 이용자의 IP 주소와 브라우저 정보가 해당 네트워크에 전달될 수 있습니다.",
            ),
        ),
        section(
            "rights", "10. 이용자의 권리와 행사 방법",
            p("정보주체는 회사에 대해 언제든지 다음의 권리를 행사할 수 있습니다."),
            bullets(
                "개인정보 <strong>열람</strong> 요구",
                "오류 등이 있을 경우 <strong>정정</strong> 요구",
                "<strong>삭제</strong> 요구",
                "<strong>처리정지</strong> 요구",
                "개인정보의 이전(전송) 요구",
            ),
            numbers(
                "<strong>행사 방법</strong> — 서비스 안의 설정 화면에서 직접 하시거나 12항의 연락처로 " +
                    "이메일을 보내 주십시오. 회사는 요구를 받은 날로부터 <strong>10일 이내</strong>에 " +
                    "조치하고 그 결과를 알려 드립니다.",
                "<strong>직접 처리할 수 있는 것</strong> — 이름과 프로필 사진은 프로필 설정에서 " +
                    "언제든지 변경할 수 있습니다. 계정 삭제는 앱과 웹의 설정 화면에서 직접 요청할 수 " +
                    "있습니다.",
                "<strong>제한</strong> — 다른 법령에서 그 개인정보가 수집 대상으로 명시되어 있는 " +
                    "경우, 다른 사람의 생명·신체를 해할 우려가 있거나 재산과 이익을 부당하게 침해할 " +
                    "우려가 있는 경우 요구가 제한될 수 있습니다. 학원이 개인정보처리자인 정보에 " +
                    "대해서는 8항을 따릅니다.",
                "법정대리인이나 위임을 받은 자를 통해서도 권리를 행사할 수 있습니다.",
            ),
        ),
        section(
            "security", "11. 개인정보의 안전성 확보조치",
            numbers(
                "<strong>접근 통제</strong> — 데이터베이스와 저장소에 보안 규칙을 적용해 이용자가 " +
                    "자신과 자신에게 허용된 범위의 데이터에만 접근할 수 있도록 합니다. 다른 학생의 " +
                    "출결 기록은 접근이 거부되는 것이 아니라 접근 가능한 경로에 존재하지 않도록 " +
                    "설계되어 있습니다.",
                "<strong>전송 구간 암호화</strong> — 모든 통신은 HTTPS로 암호화됩니다.",
                "<strong>접근 기록의 보관</strong> — 권한 변경과 개인정보 수정 등 주요 행위는 감사 " +
                    "기록으로 남습니다.",
                "<strong>최소 수집</strong> — 출결 QR의 일회용 코드는 원문을 저장하지 않고 해시값만 " +
                    "기록합니다.",
                "<strong>권한 분리</strong> — 학원 관리자 권한은 해당 학원 범위로만 한정되며, 결제로 " +
                    "플랫폼 전체 권한을 얻을 수 없습니다.",
            ),
        ),
        section(
            "officer", "12. 개인정보 보호책임자",
            table(
                listOf("구분", "내용"),
                listOf(
                    listOf("개인정보 보호책임자", "$PRIVACY_OFFICER ($PRIVACY_OFFICER_TITLE)"),
                    listOf("이메일", mailto(PRIVACY_OFFICER_EMAIL)),
                ),
            ),
            p(
                "서비스 이용 중 발생한 개인정보 보호 관련 문의, 불만처리, 피해구제를 위 연락처로 " +
                    "문의하실 수 있습니다. 회사는 지체 없이 답변하고 처리하겠습니다.",
            ),
        ),
        section(
            "remedy", "13. 권익침해 구제방법",
            p("아래 기관에 분쟁 해결이나 상담을 신청하실 수 있습니다."),
            table(
                listOf("기관", "전화", "웹사이트"),
                listOf(
                    listOf("개인정보분쟁조정위원회", "1833-6972", "www.kopico.go.kr"),
                    listOf("개인정보침해 신고센터", "118", "privacy.kisa.or.kr"),
                    listOf("대검찰청 사이버수사과", "1301", "www.spo.go.kr"),
                    listOf("경찰청 사이버수사국", "182", "ecrm.police.go.kr"),
                ),
            ),
            p(
                "「개인정보 보호법」 제35조(열람), 제36조(정정·삭제), 제37조(처리정지)에 대한 " +
                    "공공기관의 처분 또는 부작위로 권리나 이익을 침해받은 경우 행정심판을 청구할 수 " +
                    "있습니다(중앙행정심판위원회 110).",
            ),
        ),
        section(
            "changes", "14. 개인정보처리방침의 변경",
            p(
                "본 방침은 ${EFFECTIVE_DATE}부터 적용됩니다. 내용의 추가·삭제·수정이 있을 경우 시행 " +
                    "7일 전부터 서비스 안의 공지사항을 통해 알려 드립니다. 다만 이용자 권리에 중대한 " +
                    "변경이 있는 경우에는 30일 전에 알려 드립니다.",
            ),
        ),
    )

    private fun terms(): List<Section> = listOf(
        section(
            "purpose", "1. 목적과 정의",
            p(
                "본 약관은 $COMPANY(이하 \"회사\")가 제공하는 EduBridge 서비스(이하 \"서비스\")의 " +
                    "이용과 관련하여 회사와 이용자의 권리·의무 및 책임사항을 정하는 것을 목적으로 합니다.",
            ),
            bullets(
                "<strong>서비스</strong> — 학원의 출결 관리, 출결 알림, 공지·게시판, 일정 관리 기능을 " +
                    "제공하는 웹 및 모바일 애플리케이션",
                "<strong>이용자</strong> — 본 약관에 따라 서비스를 이용하는 회원",
                "<strong>학원</strong> — 서비스를 도입하여 소속 구성원을 관리하는 교육기관",
                "<strong>학원 관리자</strong> — 학원 단위로 구성원과 설정을 관리할 권한을 가진 이용자",
                "<strong>보호자</strong> — 학원의 초대를 받아 특정 학생과 연결되어 그 학생의 출결 " +
                    "알림을 받는 이용자",
                "<strong>게시물</strong> — 이용자가 서비스에 게시한 글, 댓글, 공지, 첨부 파일, 일정 등 " +
                    "일체의 정보",
            ),
        ),
        section(
            "service", "2. 서비스의 내용과 약관의 변경",
            p("회사는 다음 서비스를 제공합니다."),
            numbers(
                "QR 코드 및 수동 입력을 통한 출결 기록",
                "등원·하원·정정 발생 시 학생 본인과 연결된 보호자에게 알림 발송",
                "학원 공지 발행 및 게시판(글·댓글·첨부)",
                "학원 및 개인 일정 관리",
                "학원 구성원 초대와 권한 관리",
                "그 밖에 회사가 정하는 기능",
            ),
            p(
                "회사는 관계 법령을 위배하지 않는 범위에서 약관을 개정할 수 있으며, 개정 시 적용일자와 " +
                    "개정 사유를 명시하여 적용일자 7일 전부터 공지합니다. 이용자에게 불리한 변경의 " +
                    "경우 30일 전에 공지하고 개별 통지합니다. 개정 약관에 동의하지 않는 이용자는 " +
                    "이용계약을 해지할 수 있습니다.",
            ),
        ),
        section(
            "signup", "3. 이용계약의 성립과 가입 자격",
            numbers(
                "이용계약은 이용자가 본 약관에 동의하고 회사가 정한 절차에 따라 가입을 신청하면 회사가 " +
                    "이를 승낙함으로써 성립합니다.",
                "가입은 소셜 로그인 또는 이메일 계정으로 이루어집니다.",
            ),
            p("회사는 다음의 경우 승낙을 거부하거나 이용계약을 해지할 수 있습니다."),
            bullets(
                "<strong>만 14세 미만인 경우</strong> — 서비스는 만 14세 미만의 가입을 허용하지 않습니다.",
                "타인의 명의를 도용하거나 허위 정보를 기재한 경우",
                "이전에 본 약관 위반으로 이용계약이 해지된 이력이 있는 경우",
                "기술적으로 서비스 제공이 불가능한 경우",
            ),
        ),
        section(
            "account", "4. 계정과 권한",
            numbers(
                "가입 직후 이용자는 어떤 학원에도 소속되지 않은 상태로 시작합니다.",
                "학원 권한은 <strong>학원 관리자의 초대를 수락하거나 학원을 직접 개설</strong>하는 " +
                    "경우에만 부여됩니다.",
                "학원 권한은 <strong>해당 학원 범위 안에서만</strong> 유효합니다. 한 학원의 관리자가 " +
                    "다른 학원의 데이터에 접근할 수 없습니다.",
                "보호자는 학원 구성원 자격을 갖지 않으며, 연결된 학생의 출결 알림 수신 외에 학원 " +
                    "데이터에 접근할 수 없습니다.",
                "이용자는 계정 정보를 제3자에게 양도하거나 빌려줄 수 없으며, 계정 관리 소홀로 발생한 " +
                    "손해에 대해 책임을 집니다.",
                "하나의 계정으로 여러 기기에 동시에 로그인할 수 있습니다.",
            ),
        ),
        section(
            "academy", "5. 학원과 이용자의 관계",
            numbers(
                "학원이 서비스에 등록·관리하는 학생 명부, 출결 기록, 보호자 연락처에 대하여 " +
                    "개인정보처리자는 해당 학원이며, 회사는 학원의 위탁을 받아 처리하는 수탁자입니다.",
                "학원과 소속 이용자 사이의 교습 계약, 수강료, 학사 운영에 관한 사항은 <strong>학원과 " +
                    "이용자 사이의 문제</strong>이며 회사는 그 당사자가 아닙니다.",
                "출결 기록의 정확성에 대한 1차적 책임은 이를 입력하고 정정하는 학원에 있습니다. 회사는 " +
                    "기록과 전달의 기술적 수단을 제공합니다.",
                "학원의 서비스 이용이 종료되면 해당 학원의 데이터는 정해진 반환 준비기간을 거쳐 " +
                    "파기됩니다. 소속 이용자는 그 기간 안에 자신의 기록을 확인하고 내려받을 수 있습니다.",
            ),
        ),
        section(
            "content", "6. 게시물에 대한 권리와 책임",
            numbers(
                "게시물의 저작권은 이를 작성한 이용자에게 있습니다.",
                "이용자는 게시물을 게시함으로써, 회사가 서비스 제공·개선·백업에 필요한 범위에서 이를 " +
                    "저장·복제·전송·표시할 수 있는 권한을 부여합니다. 이 권한은 서비스 제공 목적에 " +
                    "한정되며, <strong>회사는 게시물을 외부에 판매하거나 광고에 사용하지 않습니다.</strong>",
                "게시물의 내용에 대한 책임은 작성자에게 있습니다.",
            ),
        ),
        section(
            "prohibited", "7. 금지 행위",
            p("이용자는 다음 행위를 하여서는 안 됩니다."),
            numbers(
                "타인의 개인정보를 동의 없이 게시하거나 유출하는 행위",
                "<strong>아동·청소년에게 유해하거나 이들을 대상으로 하는 성적 표현·착취·유인 행위</strong>",
                "욕설, 비방, 모욕, 명예훼손, 협박, 괴롭힘, 차별과 혐오 표현",
                "폭력적·선정적·잔혹한 내용의 게시",
                "허위 사실의 유포, 타인 사칭, 사기와 기망 행위",
                "저작권·상표권 등 타인의 지적재산권을 침해하는 행위",
                "광고·홍보·스팸의 반복 게시, 다단계나 불법 금융 상품의 권유",
                "악성코드나 바이러스가 포함된 파일의 업로드",
                "비정상적 대량 요청, 자동화 도구를 이용한 접근, 보안 규칙 우회 시도 등 서비스의 " +
                    "정상적인 운영을 방해하는 행위",
                "출결 기록을 허위로 생성하거나 타인의 출결을 대신 처리하는 행위",
                "그 밖에 관계 법령을 위반하는 행위",
            ),
        ),
        section(
            "moderation", "8. 신고, 삭제, 이용 제한",
            numbers(
                "<strong>신고</strong> — 이용자는 서비스 안의 기능을 통해 게시글·댓글 및 다른 " +
                    "이용자를 신고할 수 있습니다. 신고 내용은 해당 학원의 관리 권한을 가진 교직원에게 " +
                    "전달됩니다.",
                "<strong>삭제 권한</strong> — 작성자 본인이 삭제한 경우, 해당 학원의 교직원이 학원 " +
                    "운영상 삭제한 경우, 회사가 7항 위반이나 법령 위반을 확인한 경우에 게시물이 " +
                    "삭제되거나 노출이 제한될 수 있습니다.",
                "<strong>이용 제한</strong> — 회사는 위반의 정도에 따라 경고, 게시물 삭제, 기능 제한, " +
                    "이용계약 해지의 조치를 취할 수 있습니다. 조치 전에 사유를 통지하되 긴급한 경우 " +
                    "조치 후에 통지할 수 있습니다.",
                "<strong>이의 제기</strong> — 조치에 이의가 있는 이용자는 ${mailto(SUPPORT_EMAIL)}로 " +
                    "이의를 제기할 수 있으며, 회사는 접수 후 지체 없이 검토하고 결과를 알려 드립니다.",
                "「정보통신망 이용촉진 및 정보보호 등에 관한 법률」 제44조의2에 따라 사생활 침해나 " +
                    "명예훼손을 이유로 한 삭제 요청이 있는 경우 회사는 지체 없이 삭제 또는 임시조치를 " +
                    "취합니다.",
            ),
            callout(
                "<strong>삭제의 방식</strong> — 공지·게시글·댓글을 삭제하면 본문과 제목은 즉시 " +
                    "비워지고 삭제 표시만 남습니다. 문서 자체는 삭제 사실을 확인할 수 있도록 남아 " +
                    "있으며 목록 화면에는 표시되지 않습니다.",
            ),
        ),
        section(
            "availability", "9. 서비스의 중단과 변경",
            numbers(
                "회사는 시스템 점검, 교체, 고장, 통신 두절 등의 사유가 발생한 경우 서비스 제공을 " +
                    "일시적으로 중단할 수 있으며 사전에 공지합니다. 다만 부득이한 경우 사후에 공지할 " +
                    "수 있습니다.",
                "회사는 서비스의 내용을 변경할 수 있으며 중요한 변경은 사전에 공지합니다.",
            ),
        ),
        section(
            "fees", "10. 요금과 결제",
            numbers(
                "학생·보호자·교직원 개인은 서비스를 <strong>무료로</strong> 이용합니다.",
                "학원 운영을 위한 관리자 기능은 학원 개설자가 구매하는 월 구독으로 제공됩니다. " +
                    "요금제 구성과 가격은 앱의 구매 화면에 표시된 내용을 따릅니다.",
                "구독 결제는 Google Play 인앱결제로 처리됩니다. 회사는 카드번호 등 결제수단 정보를 " +
                    "직접 수집하거나 저장하지 않습니다.",
                "구독에는 7일 무료체험이 포함될 수 있으며, 체험이 끝나기 전에 해지하지 않으면 " +
                    "표시된 요금이 자동으로 결제됩니다. 체험 종료와 결제 예정일은 Google Play가 " +
                    "사전에 알립니다.",
                "구독은 매월 자동 갱신됩니다. 해지는 Google Play의 구독 관리 화면에서 언제든지 할 " +
                    "수 있으며, 다음 결제일 24시간 전까지 해지하면 다음 회차 요금이 청구되지 않습니다. " +
                    "해지 후에도 이미 결제한 기간이 끝날 때까지 이용할 수 있습니다.",
                "환불은 Google Play 환불 정책과 전자상거래 등에서의 소비자보호에 관한 법률이 정하는 " +
                    "바에 따릅니다.",
                "구독 만료 후의 데이터 처리(보존·열람·파기)는 개인정보처리방침과 계정 삭제 안내를 " +
                    "따릅니다.",
            ),
        ),
        section(
            "termination", "11. 계약 해지와 계정 삭제",
            numbers(
                "이용자는 언제든지 서비스 안의 설정 화면에서 계정 삭제를 요청할 수 있습니다.",
                "삭제 요청 후 <strong>7일의 유예기간</strong> 동안 요청을 취소할 수 있으며, " +
                    "유예기간이 지나면 되돌릴 수 없습니다.",
                "삭제되는 것과 남는 것은 ${link("/account-deletion", "계정 삭제 안내")}에 상세히 " +
                    "안내되어 있습니다.",
                "<strong>학원장이거나 학원의 마지막 관리자인 계정은 삭제할 수 없습니다.</strong> " +
                    "관리자가 없는 학원은 구성원을 초대하거나 권한을 부여할 방법이 영구히 사라지기 " +
                    "때문입니다. 후임 관리자를 지정한 뒤 다시 요청해 주십시오.",
            ),
        ),
        section(
            "liability", "12. 회사의 책임과 면책",
            numbers(
                "회사는 서비스를 안정적으로 제공하기 위해 노력하며 관계 법령이 정하는 범위에서 책임을 " +
                    "집니다.",
                "천재지변·전쟁·정전·기간통신사업자의 서비스 중단 등 불가항력으로 인한 장애, 이용자의 " +
                    "귀책사유(계정 정보 관리 소홀, 기기 분실, 알림 권한 거부 등)로 발생한 손해, 이용자가 " +
                    "게시한 게시물의 내용으로 발생한 분쟁, 학원과 이용자 사이의 교습 계약·수강료·학사 " +
                    "운영에 관한 분쟁에 대하여 회사는 책임을 지지 않습니다.",
                "회사의 손해배상 책임은 회사의 고의 또는 중대한 과실이 없는 한, 해당 이용자가 회사에 " +
                    "지급한 최근 3개월간의 이용료를 한도로 합니다.",
            ),
            callout(
                "<strong>알림에 관한 안내</strong> — 출결 알림은 기기의 알림 설정, 네트워크 상태, " +
                    "운영체제의 절전 정책, 푸시 서비스 사업자의 사정에 따라 지연되거나 도달하지 않을 수 " +
                    "있습니다. 회사는 발송을 시도한 기록을 남기지만 <strong>모든 알림의 도달을 " +
                    "보증하지 않습니다.</strong> 자녀의 등하원 확인은 알림에만 의존하지 마시고 서비스 " +
                    "안의 출결 기록 화면을 함께 확인해 주십시오.",
            ),
        ),
        section(
            "law", "13. 준거법과 분쟁 해결",
            numbers(
                "본 약관은 <strong>대한민국 법</strong>을 준거법으로 합니다.",
                "서비스 이용과 관련한 분쟁은 회사와 이용자가 성실히 협의하여 해결합니다.",
                "협의로 해결되지 않는 경우 「민사소송법」에 따른 관할 법원에 소를 제기할 수 있습니다.",
            ),
        ),
        section(
            "company", "14. 사업자 정보",
            table(
                listOf("구분", "내용"),
                listOf(
                    listOf("상호", COMPANY),
                    listOf("대표자", REPRESENTATIVE),
                    listOf("주소", ADDRESS),
                    listOf("사업자등록번호", BUSINESS_NUMBER),
                    listOf("통신판매업 신고번호", MAIL_ORDER_NUMBER),
                    listOf("이메일", mailto(SUPPORT_EMAIL)),
                ),
            ),
            p("본 약관은 ${EFFECTIVE_DATE}부터 시행합니다."),
        ),
    )

    private fun support(): List<Section> = listOf(
        section(
            "contact", "문의 방법",
            table(
                listOf("구분", "연락처", "응답 목표"),
                listOf(
                    listOf("이메일(주 채널)", mailto(SUPPORT_EMAIL), "영업일 기준 2일 이내"),
                    listOf(
                        "개인정보 관련 문의",
                        mailto(PRIVACY_OFFICER_EMAIL),
                        "「개인정보 보호법」에 따라 10일 이내",
                    ),
                ),
            ),
        ),
        section(
            "faq", "문의 전에 확인해 주세요",
            subheading("출결이나 반 배정이 잘못되었습니다"),
            p(
                "출결 기록과 학생 명부는 소속 학원이 관리합니다. <strong>먼저 학원에 문의해 " +
                    "주십시오.</strong> 학원 관리자와 선생님은 출결을 정정할 수 있습니다. 정정하면 원래 " +
                    "기록을 지우지 않고 정정 이력이 함께 남습니다.",
            ),
            subheading("알림이 오지 않습니다"),
            numbers(
                "기기 설정에서 EduBridge의 알림 권한이 허용되어 있는지 확인해 주십시오.",
                "앱에 로그인되어 있는지 확인해 주십시오. 로그아웃 상태에서는 알림이 오지 않습니다.",
                "배터리 절약 모드나 방해 금지 모드가 켜져 있지 않은지 확인해 주십시오.",
                "학원에 보호자 연결이 활성 상태로 등록되어 있는지 확인해 주십시오.",
            ),
            p(
                "알림은 등하원 기록이 만들어지는 즉시 발송되지만 네트워크와 운영체제 사정에 따라 " +
                    "지연되거나 도달하지 않을 수 있습니다. 실제 기록은 출결 화면에서 확인하실 수 있습니다.",
            ),
            subheading("자녀의 출결을 볼 수 없습니다"),
            p(
                "보호자는 학원이 발급한 초대를 수락해 연결된 학생의 정보만 볼 수 있습니다. 연결이 " +
                    "해지되면 이미 전달된 내역도 즉시 사라집니다. 연결 상태는 학원에 문의해 주십시오.",
            ),
            subheading("QR 스캔이 되지 않습니다"),
            p(
                "QR 코드는 보안을 위해 주기적으로 바뀝니다. 촬영해 둔 이미지나 화면 캡처로는 인식되지 " +
                    "않으며 반드시 현장의 화면을 직접 스캔해야 합니다. 같은 학생이 짧은 시간 안에 " +
                    "연속으로 스캔하는 것도 차단됩니다.",
            ),
            subheading("로그인이 되지 않습니다"),
            p(
                "가입할 때 사용한 것과 같은 로그인 방식(구글 계정 또는 이메일)으로 시도해 주십시오. " +
                "만 14세 미만으로 확인된 계정은 이용이 제한됩니다.",
            ),
            subheading("게시물을 신고하고 싶습니다"),
            p(
                "게시글과 댓글 화면의 신고 기능을 이용해 주십시오. 신고는 해당 학원의 교직원에게 " +
                    "전달됩니다. 긴급하거나 학원을 통한 처리가 어려운 경우 ${mailto(SUPPORT_EMAIL)}로 " +
                    "직접 알려 주십시오.",
            ),
            subheading("계정을 삭제하고 싶습니다"),
            p("${link("/account-deletion", "계정 삭제 안내")}를 참고해 주십시오."),
        ),
        section(
            "what-to-send", "문의하실 때 알려주시면 좋은 정보",
            p("빠른 처리를 위해 다음을 함께 보내 주십시오."),
            bullets(
                "가입에 사용한 이메일 주소",
                "소속 학원 이름",
                "사용 중인 기기와 앱 버전(예: Android 15 / EduBridge 1.0.0) 또는 웹 브라우저 종류",
                "문제가 발생한 시각과 화면",
                "오류 메시지 화면 캡처",
            ),
            callout("<strong>비밀번호는 어떤 경우에도 보내지 마십시오.</strong> 회사는 비밀번호를 묻지 않습니다."),
        ),
        section(
            "company", "사업자 정보",
            table(
                listOf("구분", "내용"),
                listOf(
                    listOf("상호", COMPANY),
                    listOf("대표자", REPRESENTATIVE),
                    listOf("주소", ADDRESS),
                    listOf("사업자등록번호", BUSINESS_NUMBER),
                ),
            ),
        ),
    )

    private fun accountDeletion(): List<Section> = listOf(
        section(
            "intro", "서비스 정보",
            table(
                listOf("구분", "내용"),
                listOf(
                    listOf("서비스명", "EduBridge"),
                    listOf("개발사", COMPANY),
                    listOf("문의", mailto(SUPPORT_EMAIL)),
                ),
            ),
            callout(
                "이 안내는 <strong>앱을 설치하지 않아도, 로그인하지 않아도</strong> 읽으실 수 있습니다.",
            ),
        ),
        section(
            "how", "1. 삭제를 요청하는 방법",
            subheading("방법 1. 앱 또는 웹에서 직접 (권장)"),
            numbers(
                "EduBridge에 로그인합니다.",
                "<strong>설정 → 계정 삭제</strong>로 이동합니다.",
                "안내를 확인하고 삭제를 요청합니다.",
            ),
            subheading("방법 2. 이메일로 요청"),
            p(
                "앱을 이미 삭제하셨거나 로그인이 어려운 경우 ${mailto(SUPPORT_EMAIL)}로 아래 내용을 " +
                    "보내 주십시오.",
            ),
            bullets(
                "제목: 계정 삭제 요청",
                "본문: 가입에 사용한 이메일 주소, 소속 학원 이름(있는 경우)",
            ),
            p(
                "회사는 본인 확인 절차를 거친 뒤 요청을 접수합니다. 확인이 완료되면 아래 2항과 동일한 " +
                    "절차가 진행됩니다.",
            ),
        ),
        section(
            "timeline", "2. 처리 기간",
            table(
                listOf("단계", "기간"),
                listOf(
                    listOf("요청 접수", "즉시"),
                    listOf("<strong>취소할 수 있는 유예기간</strong>", "<strong>7일</strong>"),
                    listOf("자동 파기 실행", "유예기간 종료 후 첫 03시(한국시간)"),
                ),
            ),
            p(
                "유예기간을 두는 이유는 실수로 누른 삭제를 되돌릴 수 있게 하기 위해서입니다. 유예기간 " +
                    "동안 계정은 평소처럼 동작하며, 로그인 후 같은 화면에서 삭제 요청을 취소할 수 " +
                    "있습니다. 유예기간이 지나 파기가 시작되면 <strong>되돌릴 수 없습니다.</strong>",
            ),
        ),
        section(
            "deleted", "3. 완전히 삭제되는 것",
            p("파기가 실행되면 다음이 복구할 수 없게 완전히 삭제됩니다."),
            bullets(
                "<strong>로그인 계정 자체</strong> — 소셜 로그인 연결이 끊기고 같은 계정으로 다시 " +
                    "로그인할 수 없습니다",
                "프로필 — 이름, 이메일, 생년월일, 프로필 사진, 소셜 로그인으로 받은 원본 정보",
                "내 출결 내역(개인 화면에 표시되던 등하원 기록)",
                "알림함의 알림 전체",
                "푸시 알림 등록 정보(등록된 모든 기기)",
                "개인 일정",
                "학원 소속 정보(멤버십)",
                "학원 개설 신청 내역",
                "저장소에 올린 프로필 사진과 업로드 중이던 파일",
            ),
        ),
        section(
            "retained", "4. 삭제되지 않는 것",
            callout(
                "계정을 삭제해도 아래는 남습니다. <strong>\"모두 즉시 완전히 삭제된다\"는 것은 사실이 " +
                    "아닙니다.</strong> 반드시 확인해 주십시오.",
            ),
            subheading("가. 학원의 학생 명부와 출결 기록 — 남습니다(연결만 끊어집니다)"),
            p(
                "학원의 수강생 명부와 출결 기록은 <strong>학원의 기록</strong>이며 학원법상 학원이 " +
                    "보존할 의무가 있습니다. EduBridge는 학원의 위탁을 받아 이를 보관하는 처리자일 " +
                    "뿐이므로 이용자 한 사람의 요청으로 학원의 장부를 삭제할 수 없습니다.",
            ),
            p(
                "계정을 삭제하면 그 기록과 내 계정 사이의 <strong>연결만 끊어집니다.</strong> 명부의 " +
                    "이름·학년·연락처와 출결 기록은 학원의 데이터로 남아, 학원의 서비스 이용이 종료될 " +
                    "때 정해진 절차에 따라 파기됩니다. 학원의 기록까지 삭제하거나 정정하기를 " +
                    "원하시면 소속 학원에 직접 요청하셔야 합니다.",
            ),
            subheading("나. 출결 이벤트 기록 — 남습니다(정정만 가능합니다)"),
            p(
                "출결의 원천 기록은 <strong>덧붙이기만 가능하고 고치거나 지울 수 없는 구조</strong>입니다. " +
                    "잘못된 기록은 지우는 것이 아니라 정정 기록을 새로 덧붙여 바로잡습니다. 따라서 원래 " +
                    "기록과 정정 기록이 함께 남습니다. 이는 출결 증빙의 신뢰성을 위한 설계이며 위 " +
                    "가항과 같이 학원의 데이터 수명주기를 따릅니다.",
            ),
            subheading("다. 게시글·댓글·공지 — 본문은 남고 작성자 표시만 지워집니다"),
            p(
                "내가 쓴 게시글·댓글·공지의 <strong>본문은 그대로 남습니다.</strong> 대신 작성자 " +
                    "이름이 지워져 누가 썼는지 알 수 없게 됩니다. 본문까지 지우지 않는 이유는 그 글에 " +
                    "다른 사람들이 참여한 대화가 함께 사라지기 때문입니다.",
            ),
            p(
                "본문 자체를 지우고 싶으시면 <strong>계정을 삭제하기 전에</strong> 해당 글과 댓글을 " +
                    "직접 삭제해 주십시오. 다만 게시물을 삭제해도 본문과 제목이 비워진 빈 기록은 " +
                    "남습니다. 목록에는 나타나지 않지만 문서 자체는 삭제 사실을 확인할 수 있도록 " +
                    "보존됩니다.",
            ),
            subheading("라. 감사 기록 — 남습니다"),
            p(
                "권한 변경과 개인정보 수정 등 주요 행위의 이력은 서비스 무결성을 위해 보존됩니다. 이 " +
                    "기록은 플랫폼 운영자만 열람할 수 있으며 「개인정보 보호법」 제21조제1항 단서에 " +
                    "따른 보존입니다.",
            ),
            subheading("마. 계정 파기 기록 — 남습니다"),
            p(
                "언제 어떤 요청으로 무엇이 몇 건 파기되었는지에 대한 기록은 남습니다. 이는 표준 " +
                    "개인정보 보호지침이 요구하는 파기 기록이며, 파기된 개인정보의 <strong>값 자체는 " +
                    "포함하지 않고 건수만</strong> 보관합니다.",
            ),
        ),
        section(
            "blocked", "5. 삭제할 수 없는 경우",
            p("다음에 해당하면 삭제 요청이 거부됩니다."),
            bullets("학원장인 계정", "학원의 마지막 관리자인 계정"),
            p(
                "관리자가 한 명도 없는 학원은 구성원을 초대하거나 권한을 부여할 방법이 영구히 사라져 " +
                    "복구할 수 없습니다. <strong>다른 관리자를 먼저 지정한 뒤</strong> 다시 요청해 " +
                    "주십시오. 요청이 거부되면 어느 학원 때문인지 화면에 표시됩니다.",
            ),
        ),
        section(
            "summary", "6. 요약",
            table(
                listOf("구분", "처리"),
                listOf(
                    listOf(
                        "로그인 계정, 프로필, 내 출결 내역, 알림, 개인 일정, 프로필 사진",
                        "<strong>완전 삭제</strong>",
                    ),
                    listOf("학원의 학생 명부·출결 기록", "남음(계정 연결만 해제)"),
                    listOf("게시글·댓글·공지 본문", "남음(작성자 이름만 삭제)"),
                    listOf("감사 기록·파기 기록", "보존"),
                    listOf("처리 기간", "요청 후 7일(그 사이 취소 가능)"),
                    listOf("문의", mailto(SUPPORT_EMAIL)),
                ),
            ),
        ),
    )
}
